// ClaimWorkspace.tsx — the claim workspace (wireframe S3, Insurance Claims
// Phase 4.5.3, redone for design-system fidelity): a sticky identity header
// (`ClaimHeader.tsx`), an ARIA tablist with live counts, the Timeline
// (claim_events — shown both inline on Overview and on its own Activity
// tab, matching the wireframe literally putting it in both places), the
// approved-vs-running marker bar, the head-wise estimate/running table, and
// a Next-actions quick-link panel. One `useClaim` query backs every tab —
// switching tabs is a render concern only, never a refetch.
import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Section, Empty } from '@/components/ui/Section';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { MarkerProgressBar } from '@/components/ui/MarkerProgressBar';
import { Timeline, type TimelineEntry } from '@/components/ui/Timeline';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { useAuth } from '@/hooks/useAuth';
import {
  useClaim, useCreateEstimate, useCreateAuthorization, useSendAuthorization,
  useCreateQuery, useReplyQuery, useUsersMap,
  useAttachClaimDocument, useWaiveClaimDocument, useClaimFormsStatus, useUpdateClaim, useUsersList,
} from './hooks';
import { RecordSubmissionDialog } from './RecordSubmissionDialog';
import { headLabel, byHeadOrder, SHEET_TABLES } from './headLabels';
import { generateProvisionalStatement } from './dischargeApi';
import { AuthorizationDecisionDialog } from './AuthorizationDecisionDialog';
import { SettlementDialog } from './SettlementDialog';
import { ClaimHeader } from './ClaimHeader';
import { DocumentUploadField } from './DocumentUploadField';
import { FormsPacketTab } from './FormsPacketTab';
import { openPatientDocument } from '@/api';
import type {
  AuthorizationKind, ClaimAuthorization, ClaimQuery, ClaimWorkspace as ClaimWorkspaceType, QueryStage,
  DocumentChecklistItem,
} from './types';

function money(v: number | string | null | undefined): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return n == null || Number.isNaN(n) ? '—' : `₹${n.toFixed(2)}`;
}

type Tab = 'overview' | 'authorizations' | 'running-bill' | 'documents' | 'queries' | 'forms-packet' | 'settlement' | 'activity';

export function ClaimWorkspacePanel({ claimId, onClose, onBack }: { claimId: number; onClose: () => void; onBack?: () => void }) {
  const query = useClaim(claimId);
  const claim = query.data;
  const [tab, setTab] = useState<Tab>('overview');

  if (query.isLoading) return <Section title="Claim"><LoadingState label="Loading claim…" /></Section>;
  if (query.isError || !claim) {
    return <Section title="Claim"><ErrorState message={query.error instanceof ApiError ? query.error.message : 'Could not load this claim'} onRetry={() => query.refetch()} /></Section>;
  }

  const authCount = claim.authorizations.length;
  // "Documents 13/14" (wireframe S3): required items that are ready, out of
  // the required items that apply to this admission.
  const requiredDocs = claim.document_checklist.filter((d) => d.required && d.effective_status !== 'not_applicable');
  const docsAttached = requiredDocs.filter((d) => d.effective_status !== 'missing').length;
  const docsTotal = requiredDocs.length;
  const openQueries = claim.queries.filter((q) => q.status === 'open').length;

  // No bordered-card wrapper around the header any more — ClaimHeader bleeds
  // past the page's own padding to reach the true top when sticky (see its
  // own header comment), and a card border can't stretch to follow a bled
  // child. Individual sections below (Section/Table) already supply their
  // own card chrome, matching the IP Case Sheet's own un-boxed layout.
  return (
    <div>
      <ClaimHeader claim={claim} onClose={onClose} onBack={onBack} />
      <div>
        {/* Wireframe S3 `.tabs`: folder tabs on a 2px rule, 10px above and
            below — the panel's own default top margin is cancelled so the
            gap below the rule is exactly the list's mb-2.5. */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className={`[&>[role=tabpanel]]:mt-0 ${SHEET_TABLES}`}>
          <TabsList variant="folder" className="mb-2.5">
            <TabsTrigger variant="folder" value="overview">Overview</TabsTrigger>
            <TabsTrigger variant="folder" value="authorizations">Authorisations ({authCount})</TabsTrigger>
            <TabsTrigger variant="folder" value="running-bill">Running bill</TabsTrigger>
            <TabsTrigger variant="folder" value="documents">Documents {docsAttached}/{docsTotal}</TabsTrigger>
            <TabsTrigger variant="folder" value="queries">Queries ({openQueries} open)</TabsTrigger>
            <TabsTrigger variant="folder" value="forms-packet">Forms &amp; packet</TabsTrigger>
            <TabsTrigger variant="folder" value="settlement">Settlement</TabsTrigger>
            <TabsTrigger variant="folder" value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><OverviewTab claim={claim} onGotoTab={setTab} /></TabsContent>
          <TabsContent value="authorizations"><AuthorizationsTab claim={claim} /></TabsContent>
          <TabsContent value="running-bill"><RunningBillTab claim={claim} /></TabsContent>
          <TabsContent value="documents">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <SubmissionSection claim={claim} />
              <DocumentChecklistSection claim={claim} />
            </div>
          </TabsContent>
          <TabsContent value="queries"><QueriesSection claim={claim} /></TabsContent>
          <TabsContent value="forms-packet"><FormsPacketTab claim={claim} /></TabsContent>
          <TabsContent value="settlement"><SettlementTab claim={claim} /></TabsContent>
          <TabsContent value="activity">
            <Section title="Activity">
              <ClaimTimeline claim={claim} />
            </Section>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/** Shared by the Overview tab's own Timeline card and the Activity tab —
 * the wireframe shows the exact same event history in both places (Overview
 * for at-a-glance review, Activity as its own dedicated full view), not two
 * different slices of it. */
function ClaimTimeline({ claim }: { claim: ClaimWorkspaceType }) {
  const usersMap = useUsersMap();
  const EVENT_LABEL: Record<string, string> = {
    auto_created: 'Claim auto-created', created: 'Claim created', authorization_created: 'Authorization drafted', authorization_sent: 'Authorization sent',
    authorization_decided: 'Authorization decided', query_logged: 'Query logged', query_replied: 'Query replied',
    submission_recorded: 'Submission recorded', settlement_posted: 'Settlement posted', discharged: 'Discharged',
    discharged_before_final_approval: 'Discharged (Admin override — before final approval)',
  };
  // Matches the wireframe's own colour language: green for a positive
  // outcome (coverage captured, an approval, a cleanly-resolved query/
  // settlement), amber for something that needs attention (a payer query
  // just logged, an override, a settlement that came back short), teal for
  // a plain milestone (sent/drafted/recorded), red for an outright denial.
  // `authorization_decided`/`settlement_posted` read the OUTCOME out of the
  // event's own `details` rather than colouring every occurrence the same —
  // a denied pre-auth and an approved one are the same event name but very
  // different news.
  function dotToneFor(e: ClaimWorkspaceType['events'][number]): 'success' | 'warn' | 'danger' | 'neutral' {
    const details = e.details ?? {};
    switch (e.event) {
      case 'auto_created': return 'success';
      case 'query_logged': return 'warn';
      case 'query_replied': return 'success';
      case 'discharged': return 'success';
      case 'discharged_before_final_approval': return 'warn';
      case 'authorization_decided': {
        const decision = details.decision;
        if (decision === 'denied') return 'danger';
        if (decision === 'approved' || decision === 'partial') return 'success';
        return 'neutral';
      }
      case 'settlement_posted': {
        const deductionTotal = Number(details.deduction_total ?? 0);
        return deductionTotal > 0 ? 'warn' : 'success';
      }
      default: return 'neutral';
    }
  }
  const entries: TimelineEntry[] = claim.events.map((e) => ({
    key: String(e.id),
    at: new Date(e.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
    title: EVENT_LABEL[e.event] ?? e.event,
    dotTone: dotToneFor(e),
    meta: [
      e.actor_user_id != null ? (usersMap.get(e.actor_user_id) ?? `User #${e.actor_user_id}`) : null,
      e.from_status && e.to_status && e.from_status !== e.to_status ? `${e.from_status} → ${e.to_status}` : null,
    ].filter(Boolean).join(' · '),
  }));
  return <Timeline entries={entries} emptyLabel="No activity recorded yet." />;
}

// ── Overview: summary, approved-vs-running bar, estimate, next actions ──

function OverviewTab({ claim, onGotoTab }: { claim: ClaimWorkspaceType; onGotoTab: (t: Tab) => void }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState('');
  const openFinalAuth = claim.authorizations.find((a) => a.kind === 'final' && ['sent', 'query'].includes(a.status)) || null;
  const [decisionAuth, setDecisionAuth] = useState<ClaimAuthorization | null>(null);

  const regenerate = async () => {
    if (!claim.admission_id) return;
    setRegenerating(true); setRegenError('');
    try {
      await generateProvisionalStatement(claim.admission_id);
      await queryClient.invalidateQueries({ queryKey: keys.claims.detail(claim.id) });
    } catch (e) { setRegenError(e instanceof Error ? e.message : String(e)); }
    setRegenerating(false);
  };

  const approvedTotal = parseFloat(claim.approved_total) || 0;
  const runningNet = claim.running_bill?.net ?? null;
  // The marker shows where the approved amount sits as a fraction of the
  // running bill (wireframe's own semantic: a full track representing the
  // bill, a tick at the approved position) — not a fill proportion.
  const markerPct = runningNet != null && runningNet > 0 ? Math.round((approvedTotal / runningNet) * 100) : undefined;
  // "(initial 40,000 + enhancement 30,000)" — the same rule the server uses
  // for approved_total: the latest approved initial plus every approved
  // enhancement (routes/claims.js's decision handler).
  const approvedAuths = claim.authorizations.filter((a) => ['approved', 'partial'].includes(a.status) && a.approved_amount != null);
  const latestInitial = [...approvedAuths].filter((a) => a.kind === 'initial').pop();
  const enhancements = approvedAuths.filter((a) => a.kind === 'enhancement');
  const breakdown = [
    latestInitial ? `initial ${money(latestInitial.approved_amount)}` : null,
    ...enhancements.map((a) => `enhancement ${money(a.approved_amount)}`),
  ].filter(Boolean).join(' + ');
  const finalRequest = claim.authorizations.find((a) => a.kind === 'final' && a.status !== 'draft') || null;
  const billPctOfApproved = runningNet != null && approvedTotal > 0 ? Math.round((runningNet / approvedTotal) * 100) : null;
  const postDischarge = ['discharged', 'submitted', 'claim_query', 'settled', 'short_settled', 'appealed', 'closed', 'documents_issued'].includes(claim.status);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[7fr_3fr]">
      <div className="flex flex-col gap-4">
        <Section title="Approved vs running bill">
          <div className="flex flex-wrap items-center justify-between gap-x-4 text-[12.5px]">
            <span>Approved so far <b>{money(claim.approved_total)}</b>{breakdown && <span className="text-ink-muted"> ({breakdown})</span>}</span>
            {runningNet != null && (
              <span>
                Provisional bill <b>{money(runningNet)}</b>
                {finalRequest && <> · requested final <b>{money(finalRequest.requested_amount)}</b></>}
              </span>
            )}
          </div>
          {runningNet != null && runningNet > 0 && (
            <MarkerProgressBar className="my-1.5" markerPct={markerPct} />
          )}
          {runningNet != null && runningNet > 0 && (
            <div className="text-[11px] text-ink-muted">
              The marker shows the approved amount.
              {billPctOfApproved != null ? <> Bill is <b>{billPctOfApproved}%</b> of approved.</> : ' Nothing approved yet.'}
              {finalRequest?.sent_at && <> Final authorisation requested <b>{new Date(finalRequest.sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</b>.</>}
            </div>
          )}
          {claim.enhancement?.due && (
            <div className="mt-1 text-[11px] text-warn-ink">
              Running bill is {Math.round((claim.enhancement.ratio ?? 0) * 100)}% of approved — consider raising an enhancement.
            </div>
          )}
          <TatChips tat={claim.tat} />
          <HeadWiseTable claim={claim} />
        </Section>
        <Section title="Timeline">
          <ClaimTimeline claim={claim} />
        </Section>
        <EstimateSection claim={claim} />
      </div>
      <div className="flex flex-col gap-3">
        <Section title="Next actions">
          <div className="flex flex-col items-start gap-1.5">
            {openFinalAuth && (
              <Button size="sm" onClick={() => setDecisionAuth(openFinalAuth)}>Record final decision</Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onGotoTab('queries')}>Log TPA query</Button>
            {claim.admission_id && (
              <Button size="sm" variant="ghost" loading={regenerating} onClick={regenerate}>Regenerate provisional bill</Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onGotoTab('forms-packet')}>Print pre-auth form →</Button>
            <Button
              size="sm" variant="ghost" disabled={!postDischarge}
              title={postDischarge ? undefined : 'Available after discharge'}
              onClick={() => onGotoTab('forms-packet')}
            >
              Build claim packet →
            </Button>
            {!postDischarge && <span className="-mt-1 text-[10.5px] text-ink-muted">Claim packet: available after discharge</span>}
            {regenError && <div className="text-[11px] text-danger-ink">{regenError}</div>}
            {user?.modules?.includes('claims:settle') && ['discharged', 'submitted', 'short_settled'].includes(claim.status) && (
              <Button size="sm" variant="ghost" onClick={() => onGotoTab('settlement')}>Go to settlement →</Button>
            )}
          </div>
        </Section>
        <ClaimDetailsSection claim={claim} />
        <DocumentChecklistSection claim={claim} compact onOpen={() => onGotoTab('documents')} />
        <FixListSection claimId={claim.id} />
      </div>
      {decisionAuth && (
        <AuthorizationDecisionDialog claim={claim} auth={decisionAuth} open onClose={() => setDecisionAuth(null)} />
      )}
    </div>
  );
}

/** The desk-editable claim fields the forms read (PATCH /claims/:id): the
 * treating doctor (pre-auth/Part B doctor block — the fix-list points here),
 * the payer's own claim no. and the expected length of stay. */
function ClaimDetailsSection({ claim }: { claim: ClaimWorkspaceType }) {
  const users = useUsersList();
  const mutation = useUpdateClaim(claim.id);
  const [doctorId, setDoctorId] = useState(claim.treating_doctor_user_id != null ? String(claim.treating_doctor_user_id) : '');
  const [externalNo, setExternalNo] = useState(claim.external_claim_no ?? '');
  const [los, setLos] = useState(claim.expected_los_days != null ? String(claim.expected_los_days) : '');
  const [msg, setMsg] = useState('');
  const doctors = (users.data ?? []).filter((u) => u.active !== false && /doctor/i.test(u.role));
  const dirty = doctorId !== (claim.treating_doctor_user_id != null ? String(claim.treating_doctor_user_id) : '')
    || externalNo !== (claim.external_claim_no ?? '') || los !== (claim.expected_los_days != null ? String(claim.expected_los_days) : '');
  const save = async () => {
    setMsg('');
    try {
      await mutation.mutateAsync({
        treating_doctor_user_id: doctorId ? Number(doctorId) : null,
        external_claim_no: externalNo.trim() || null,
        expected_los_days: los ? Number(los) : null,
      });
      setMsg('Saved');
    } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
  };
  return (
    <Section title="Claim details">
      <div className="flex flex-col gap-2">
        <FormField label="Treating doctor">
          <NativeSelect value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
            <option value="">Not set</option>
            {doctors.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </NativeSelect>
        </FormField>
        <FormField label="Payer's claim no.">
          <Input value={externalNo} onChange={(e) => setExternalNo(e.target.value)} placeholder="From the TPA / insurer" />
        </FormField>
        <FormField label="Expected stay (days)">
          <Input type="number" min={1} value={los} onChange={(e) => setLos(e.target.value)} className="w-28" />
        </FormField>
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={!dirty} loading={mutation.isPending} onClick={save}>Save</Button>
          {msg && <span className={`text-[11.5px] ${msg === 'Saved' ? 'text-success-ink' : 'text-danger-ink'}`}>{msg}</span>}
        </div>
      </div>
    </Section>
  );
}

/** "Fix-list for forms" (wireframe S3): every mandatory field the pre-auth
 * request or Part B is missing RIGHT NOW, de-duplicated, each with where to
 * fix it — from GET /claims/:id/forms (the same `meta.missing` list the
 * renderers use to decide DRAFT). */
function FixListSection({ claimId }: { claimId: number }) {
  const q = useClaimFormsStatus(claimId);
  const data = q.data;
  const fields = new Map<string, { reason: string; fix_at: string | null; forms: string[] }>();
  if (data) {
    for (const [form, label] of [['preauth', 'pre-auth'], ['part_b', 'Part B']] as const) {
      for (const m of data[form].missing) {
        const hit = fields.get(m.field);
        if (hit) hit.forms.push(label); else fields.set(m.field, { reason: m.reason, fix_at: m.fix_at, forms: [label] });
      }
    }
  }
  return (
    <Section title="Fix-list for forms">
      {q.isLoading && <div className="text-[12px] text-ink-muted">Checking mandatory fields…</div>}
      {q.isError && <div className="text-[12px] text-danger-ink">Could not check the forms' mandatory fields.</div>}
      {data && fields.size === 0 && (
        <div className="text-[12px] text-success-ink">All pre-auth and Part B mandatory fields resolved ✔</div>
      )}
      {data && fields.size > 0 && (
        <ul className="flex flex-col gap-1 text-[12px]">
          {[...fields.entries()].map(([field, f]) => (
            <li key={field}>
              <span className="text-warn-ink">{f.reason}</span>
              {f.fix_at && <span className="text-ink-muted"> → {f.fix_at}</span>}
              <span className="text-[10.5px] text-ink-muted"> ({f.forms.join(', ')})</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** Head/Estimate/Running — the wireframe's 3-column table. "Estimate" is the
 * latest estimate version's lines grouped by head; "Running" is the live
 * provisional statement's own heads. Different sources (one is a plan, the
 * other is what's actually been charged), joined client-side purely for
 * display — neither is recomputed here. */
function HeadWiseTable({ claim }: { claim: ClaimWorkspaceType }) {
  const estimateByHead = new Map<string, number>();
  for (const l of claim.latest_estimate?.lines ?? []) estimateByHead.set(l.head, (estimateByHead.get(l.head) ?? 0) + l.amount);
  const runningByHead = new Map<string, number>();
  const nonPayableByHead = new Map<string, number>();
  for (const h of claim.running_bill?.heads ?? []) {
    runningByHead.set(h.head, h.net);
    if (h.non_payable) nonPayableByHead.set(h.head, h.non_payable);
  }
  const heads = [...new Set([...estimateByHead.keys(), ...runningByHead.keys()])].sort(byHeadOrder);
  const nonPayableTotal = [...nonPayableByHead.values()].reduce((s, v) => s + v, 0);

  if (heads.length === 0) return null;
  return (
    <Table className="mt-2">
      <TableHeader>
        <TableRow><TableHead>Head</TableHead><TableHead className="text-right">Estimate</TableHead><TableHead className="text-right">Running</TableHead><TableHead className="text-right">Non-payable</TableHead></TableRow>
      </TableHeader>
      <TableBody>
        {heads.map((h) => (
          <TableRow key={h}>
            <TableCell>{headLabel(h)}</TableCell>
            <TableCell className="text-right tabular-nums">{money(estimateByHead.get(h) ?? 0)}</TableCell>
            <TableCell className="text-right tabular-nums">{money(runningByHead.get(h) ?? 0)}</TableCell>
            <TableCell className="text-right tabular-nums">{nonPayableByHead.get(h) ? money(nonPayableByHead.get(h)) : '—'}</TableCell>
          </TableRow>
        ))}
        <TableRow className="font-semibold">
          <TableCell>Total</TableCell>
          <TableCell className="text-right tabular-nums">{money([...estimateByHead.values()].reduce((s, v) => s + v, 0))}</TableCell>
          <TableCell className="text-right tabular-nums">{money([...runningByHead.values()].reduce((s, v) => s + v, 0))}</TableCell>
          <TableCell className="text-right tabular-nums">{nonPayableTotal ? money(nonPayableTotal) : '—'}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

function TatChips({ tat }: { tat: ClaimWorkspaceType['tat'] }) {
  if (!tat.authorization && !tat.query && !tat.submission_ageing) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {tat.authorization && (
        <Badge variant={tat.authorization.breached ? 'danger' : tat.authorization.amber ? 'caution' : 'neutral'}>
          {tat.authorization.kind} auth: {tat.authorization.elapsed_min}/{tat.authorization.target_min} min
        </Badge>
      )}
      {tat.query && (
        <Badge variant={tat.query.breached ? 'danger' : 'neutral'}>
          Query reply {tat.query.replied ? 'done' : tat.query.breached ? 'overdue' : `${tat.query.hours_remaining}h left`}
        </Badge>
      )}
      {tat.submission_ageing && (
        <Badge variant={tat.submission_ageing.breached ? 'danger' : 'neutral'}>
          Submission ageing {tat.submission_ageing.elapsed_days}/{tat.submission_ageing.target_days}d
        </Badge>
      )}
    </div>
  );
}

function EstimateSection({ claim }: { claim: ClaimWorkspaceType }) {
  const [losDays, setLosDays] = useState(String(claim.expected_los_days ?? ''));
  const [error, setError] = useState('');
  const mutation = useCreateEstimate(claim.id);

  const submit = async () => {
    setError('');
    try { await mutation.mutateAsync({ expected_los_days: losDays ? Number(losDays) : undefined }); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <Section title="Estimate (tariff × LOS)">
      {claim.latest_estimate ? (
        <div className="text-[13px]">
          <div>Version {claim.latest_estimate.version} — total {money(claim.latest_estimate.total)}</div>
          <ul className="mt-1 list-inside list-disc text-ink-muted">
            {claim.latest_estimate.lines.map((l, i) => (
              <li key={i}>{l.head}: {money(l.amount)} ({l.basis === 'per_day' ? `${l.days}d × ${money(l.rate)}` : `${l.qty ?? 1} × ${money(l.rate)}`})</li>
            ))}
          </ul>
        </div>
      ) : <div className="text-[13px] text-ink-muted">No estimate yet.</div>}
      <div className="mt-2 flex items-end gap-2">
        <FormField label="Expected LOS (days)">
          <Input value={losDays} onChange={(e) => setLosDays(e.target.value)} type="number" min={1} className="w-28" />
        </FormField>
        <Button size="sm" loading={mutation.isPending} onClick={submit}>+ New estimate version</Button>
      </div>
      {error && <div className="mt-1 text-[12px] text-danger-ink">{error}</div>}
    </Section>
  );
}

// ── Authorisations tab ──────────────────────────────────────────────────

const AUTH_KIND_LABEL: Record<AuthorizationKind, string> = { initial: 'Initial pre-auth', enhancement: 'Enhancement', final: 'Final authorisation' };

function AuthorizationsTab({ claim }: { claim: ClaimWorkspaceType }) {
  const [kind, setKind] = useState<AuthorizationKind>('initial');
  const [requestedAmount, setRequestedAmount] = useState('');
  const [error, setError] = useState('');
  const createMutation = useCreateAuthorization(claim.id);
  const sendMutation = useSendAuthorization(claim.id);
  const [decisionAuth, setDecisionAuth] = useState<ClaimAuthorization | null>(null);

  const createDraft = async () => {
    setError('');
    if (!(Number(requestedAmount) > 0)) { setError('A positive requested amount is required'); return; }
    try {
      await createMutation.mutateAsync({ kind, requested_amount: Number(requestedAmount) });
      setRequestedAmount('');
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const send = async (authId: number) => {
    setError('');
    try { await sendMutation.mutateAsync({ authId, input: {} }); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const usersMap = useUsersMap();
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
  const turnaround = (a: ClaimAuthorization) => {
    if (!a.sent_at) return '—';
    const end = a.decided_at ? new Date(a.decided_at) : new Date();
    const mins = Math.max(0, Math.round((end.getTime() - new Date(a.sent_at).getTime()) / 60000));
    const text = mins >= 60 ? `${Math.floor(mins / 60)} h ${mins % 60} m` : `${mins} min`;
    return a.decided_at ? text : `${text} (open)`;
  };
  const statusVariant = (st: string): 'neutral' | 'success' | 'warn' | 'danger' | 'info' =>
    st === 'approved' ? 'success' : st === 'partial' || st === 'query' ? 'warn' : st === 'denied' ? 'danger' : st === 'sent' ? 'info' : 'neutral';

  return (
    <Section title="Authorisations" subtitle="Initial pre-auth, enhancements and the final discharge authorisation, oldest first.">
      {claim.authorizations.length === 0 ? <Empty label="No authorisation drafted yet." /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kind</TableHead><TableHead className="text-right">Requested</TableHead><TableHead>Status</TableHead>
              <TableHead>Sent</TableHead><TableHead>Decided</TableHead><TableHead>Turnaround</TableHead>
              <TableHead className="text-right">Approved</TableHead><TableHead>Reference</TableHead><TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {claim.authorizations.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{AUTH_KIND_LABEL[a.kind]}</TableCell>
                <TableCell className="text-right tabular-nums">{money(a.requested_amount)}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(a.status)}>{a.status}</Badge>
                  {(a.deductions?.length ?? 0) > 0 && <div className="mt-0.5 text-[10.5px] text-ink-muted">{a.deductions!.length} deduction{a.deductions!.length === 1 ? '' : 's'}</div>}
                </TableCell>
                <TableCell className="text-[12px]">{fmt(a.sent_at)}{a.sent_by_user_id != null ? <div className="text-[10.5px] text-ink-muted">{usersMap.get(a.sent_by_user_id) ?? ''}</div> : null}</TableCell>
                <TableCell className="text-[12px]">{fmt(a.decided_at)}</TableCell>
                <TableCell className="text-[12px] tabular-nums">{turnaround(a)}</TableCell>
                <TableCell className="text-right tabular-nums">{a.approved_amount != null ? money(a.approved_amount) : '—'}</TableCell>
                <TableCell className="text-[12px]">{a.external_ref ?? '—'}</TableCell>
                <TableCell className="text-right">
                  {a.status === 'draft' && <Button size="sm" variant="ghost" loading={sendMutation.isPending} onClick={() => send(a.id)}>Send</Button>}
                  {(a.status === 'sent' || a.status === 'query') && (
                    <Button size="sm" onClick={() => setDecisionAuth(a)}>Record decision</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <FormField label="New authorisation">
          <NativeSelect value={kind} onChange={(e) => setKind(e.target.value as AuthorizationKind)} className="w-44">
            {Object.entries(AUTH_KIND_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </NativeSelect>
        </FormField>
        <FormField label="Requested amount ₹">
          <Input value={requestedAmount} onChange={(e) => setRequestedAmount(e.target.value)} type="number" min={0} className="w-36" />
        </FormField>
        <Button size="sm" loading={createMutation.isPending} onClick={createDraft}>+ New draft</Button>
      </div>
      {error && <div className="mt-1 text-[12px] text-danger-ink">{error}</div>}
      {decisionAuth && (
        <AuthorizationDecisionDialog claim={claim} auth={decisionAuth} open onClose={() => setDecisionAuth(null)} />
      )}
    </Section>
  );
}

// ── Running bill tab ────────────────────────────────────────────────────

function RunningBillTab({ claim }: { claim: ClaimWorkspaceType }) {
  if (!claim.running_bill) {
    return <Section title="Running bill"><div className="text-[13px] text-ink-muted">No live running bill — this claim is past discharge; see the frozen Main/Breakup bill instead.</div></Section>;
  }
  const rb = claim.running_bill;
  return (
    <Section
      title="Running bill (live, unsaved)"
      action={claim.enhancement?.due && <Badge variant="warn">Enhancement due ({Math.round((claim.enhancement.ratio ?? 0) * 100)}%)</Badge>}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Head</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Discount</TableHead>
            <TableHead className="text-right">GST</TableHead><TableHead className="text-right">Net</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...rb.heads].sort((a, b) => byHeadOrder(a.head, b.head)).map((h) => (
            <TableRow key={h.head}>
              <TableCell>{headLabel(h.head)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(h.gross)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(h.discount)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(h.gst)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(h.net)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="font-semibold">
            <TableCell>Total</TableCell>
            <TableCell className="text-right tabular-nums">{money(rb.heads.reduce((s, h) => s + h.gross, 0))}</TableCell>
            <TableCell className="text-right tabular-nums">{money(rb.heads.reduce((s, h) => s + h.discount, 0))}</TableCell>
            <TableCell className="text-right tabular-nums">{money(rb.heads.reduce((s, h) => s + h.gst, 0))}</TableCell>
            <TableCell className="text-right tabular-nums">{money(rb.net)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Section>
  );
}

// ── Queries tab ─────────────────────────────────────────────────────────

const QUERY_STAGE_LABEL: Record<QueryStage, string> = { preauth: 'Pre-auth', final: 'Final auth', claim: 'Claim' };

function QueriesSection({ claim }: { claim: ClaimWorkspaceType }) {
  const [stage, setStage] = useState<QueryStage>('preauth');
  const [queryText, setQueryText] = useState('');
  const [error, setError] = useState('');
  const createMutation = useCreateQuery(claim.id);
  const replyMutation = useReplyQuery(claim.id);

  const logQuery = async () => {
    setError('');
    if (!queryText.trim()) { setError('Query text is required'); return; }
    try {
      await createMutation.mutateAsync({ stage, received_at: new Date().toISOString(), query_text: queryText.trim() });
      setQueryText('');
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <Section title="Queries">
      <div className="flex flex-col gap-2">
        {claim.queries.length === 0 && <Empty label="None yet." />}
        {claim.queries.map((q) => (
          <QueryRow
            key={q.id} query={q}
            onReply={async (id, text) => {
              setError('');
              try { await replyMutation.mutateAsync({ queryId: id, input: { reply_text: text } }); }
              catch (e) { setError(e instanceof Error ? e.message : String(e)); }
            }}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <div className="flex items-end gap-2">
          <FormField label="Stage">
            <NativeSelect value={stage} onChange={(e) => setStage(e.target.value as QueryStage)} className="w-36">
              {Object.entries(QUERY_STAGE_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </NativeSelect>
          </FormField>
        </div>
        <Textarea value={queryText} onChange={(e) => setQueryText(e.target.value)} placeholder="Query text from the payer" rows={2} />
        <Button size="sm" loading={createMutation.isPending} onClick={logQuery}>Log query</Button>
      </div>
      {error && <div className="mt-1 text-[12px] text-danger-ink">{error}</div>}
    </Section>
  );
}

function QueryRow({ query, onReply }: { query: ClaimQuery; onReply: (id: number, text: string) => Promise<void> }) {
  const [replyText, setReplyText] = useState('');
  return (
    <div className="flex flex-col gap-1 rounded-[var(--radius)] bg-muted px-2 py-2 text-[13px]">
      <div className="flex items-center justify-between">
        <span className="font-medium">{QUERY_STAGE_LABEL[query.stage]} query</span>
        <Badge variant={query.status === 'open' ? 'warn' : 'success'}>{query.status}</Badge>
      </div>
      <div>{query.query_text}</div>
      {query.status === 'open' ? (
        <div className="flex items-end gap-2">
          <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Reply text" rows={2} className="flex-1" />
          <Button size="sm" onClick={() => onReply(query.id, replyText)} disabled={!replyText.trim()}>Reply</Button>
        </div>
      ) : (
        <div className="text-ink-muted">Reply: {query.reply_text}</div>
      )}
    </div>
  );
}

// ── Documents: submissions + the document checklist stub ───────────────

function SubmissionSection({ claim }: { claim: ClaimWorkspaceType }) {
  const [open, setOpen] = useState(false);
  const CHANNEL: Record<string, string> = { portal: 'Portal', email: 'Email', courier: 'Courier', hand: 'By hand' };
  return (
    <Section
      title="Submissions"
      action={claim.status === 'discharged' && <Button size="sm" onClick={() => setOpen(true)}>Record submission…</Button>}
    >
      {claim.submissions.length === 0 ? <Empty label="Not submitted yet." hint={claim.status === 'discharged' ? undefined : 'A claim is submitted after discharge.'} /> : (
        <Table>
          <TableHeader>
            <TableRow><TableHead>Submitted</TableHead><TableHead>Channel</TableHead><TableHead>Docket / ref</TableHead><TableHead className="text-right">Amount claimed</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {claim.submissions.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="text-[12px]">{new Date(s.submitted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</TableCell>
                <TableCell>{CHANNEL[s.channel] ?? s.channel}</TableCell>
                <TableCell>{s.docket_ref ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{money(s.amount_claimed)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {open && <RecordSubmissionDialog claim={claim} open onClose={() => setOpen(false)} />}
    </Section>
  );
}

// doc_key -> patient_documents.doc_category — what DocumentUploadField's
// file picker tags an attached file as. Matches backend/routes/
// claims-documents.js's own doc_category choices for the two auto-generated
// keys (part_b/preauth_request); every other key here has no auto_source,
// so this is the category a manual upload gets filed under.
const DOC_KEY_CATEGORY: Record<string, string> = {
  part_b: 'Claim Form', preauth_request: 'Pre-auth Request', preauth_approval: 'Pre-auth Approval',
  photo_id: 'ID Proof', discharge_summary: 'Discharge Summary', ot_notes: 'OT Notes',
  main_bill: 'Bill', breakup_bill: 'Bill', tax_invoices: 'Bill', pharmacy_bills: 'Bill',
  investigation_reports: 'Lab Report', imaging_reports: 'X-Ray / Scan', ecg: 'ECG / Echo',
  mlc_fir: 'MLC / FIR', death_summary: 'Death Summary', reference_slip: 'Other', other: 'Other',
};

const STATUS_BADGE: Record<DocumentChecklistItem['effective_status'], { variant: 'neutral' | 'success' | 'warn'; label: string }> = {
  missing: { variant: 'neutral', label: 'Missing' },
  attached: { variant: 'success', label: 'Attached' },
  generated: { variant: 'success', label: 'Generated' },
  waived: { variant: 'warn', label: 'Waived' },
  not_applicable: { variant: 'neutral', label: 'N/A' },
};

// Where a missing item comes from — shown under its label so staff know
// whether to upload or to go and generate something. Each line mirrors an
// AUTO_RESOLVERS entry in backend/utils/claimDocuments.js; a key with no
// resolver has no hint (upload only).
const DOC_KEY_HINT: Record<string, string> = {
  part_b: 'Fills in when Claim Form Part B is generated in Forms & packet, or attach a signed scan.',
  preauth_request: 'Fills in when the pre-auth form is generated in Forms & packet, or attach a signed scan.',
  preauth_approval: 'Fills in from the decision letter of an approved authorisation.',
  photo_id: 'Fills in from the ID proof or card captured with the coverage.',
  main_bill: 'Fills in once the Main bill PDF is generated.',
  breakup_bill: 'Fills in once the Breakup bill PDF is generated.',
  discharge_summary: 'Fills in when the discharge summary document is filed.',
  death_summary: 'Fills in when the death summary document is filed.',
  investigation_reports: "Fills in from the patient's lab reports.",
};

type DocState = 'need' | 'optional' | 'ready' | 'waived' | 'na';
function docState(doc: DocumentChecklistItem): DocState {
  if (doc.effective_status === 'missing') return doc.required ? 'need' : 'optional';
  if (doc.effective_status === 'waived') return 'waived';
  if (doc.effective_status === 'not_applicable') return 'na';
  return 'ready';
}

/** Status disc at the start of a row — the icon carries the state so the
 * badge on the right can be dropped where the group heading already says it. */
function DocIcon({ state }: { state: DocState }) {
  const base = 'inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold';
  if (state === 'need') return <span aria-hidden className={cn(base, 'border-[1.5px] border-solid border-warn text-warn-ink')}>!</span>;
  if (state === 'optional') return <span aria-hidden className={cn(base, 'border-[1.5px] border-solid border-border text-ink-muted')} />;
  if (state === 'waived') return <span aria-hidden className={cn(base, 'border-[1.5px] border-solid border-warn bg-warn-soft text-warn-ink')}>–</span>;
  if (state === 'na') return <span aria-hidden className={cn(base, 'bg-muted text-ink-muted')}>–</span>;
  return <span aria-hidden className={cn(base, 'bg-success text-white')}>✓</span>;
}

/** One checklist row (Insurance Claims Phase 5.4: real attach/waive). Layout
 * per wireframe S3b: icon · label (+ Required tag, helper/waive text) · actions
 * or badge on the right, wrapping under the label when the card is narrow.
 * `compact` (Overview rail) shows the label only. */
function DocumentChecklistRow({ claim, doc, compact }: { claim: ClaimWorkspaceType; doc: DocumentChecklistItem; compact?: boolean }) {
  const [waiving, setWaiving] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const attachMutation = useAttachClaimDocument(claim.id);
  const waiveMutation = useWaiveClaimDocument(claim.id);
  const state = docState(doc);
  const badge = STATUS_BADGE[doc.effective_status];
  const missing = doc.effective_status === 'missing';
  const hasDoc = doc.document_id != null;

  const doWaive = async () => {
    setError('');
    if (!reason.trim()) { setError('A reason is required'); return; }
    try {
      await waiveMutation.mutateAsync({ checklistId: doc.id, waive: true, waivedReason: reason.trim() });
      setWaiving(false); setReason('');
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const detail = state === 'waived' && doc.waived_reason ? `Waived: ${doc.waived_reason}`
    : state === 'na' ? 'Not applicable to this claim.'
    : missing ? DOC_KEY_HINT[doc.doc_key]
    : undefined;

  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 text-[12.5px]',
        compact || !missing ? 'py-2' : 'py-2.5',
        // Required-and-missing rows carry an amber accent on the left edge.
        state === 'need' && 'shadow-[inset_3px_0_0_var(--color-warn)]',
        waiving && 'bg-warn-soft/40',
      )}
    >
      <DocIcon state={state} />
      <div className={cn('min-w-0 flex-[1_1_13rem]', state === 'na' && 'text-ink-muted')}>
        <span className="font-medium">{doc.label}</span>
        {state === 'need' && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-warn-ink">Required</span>}
        {state === 'optional' && !compact && <span className="ml-1.5 text-[11px] text-ink-muted">· optional</span>}
        {detail && !compact && <div className="mt-0.5 text-[11.5px] leading-snug text-ink-muted">{detail}</div>}
      </div>

      {!compact && (
        <div className="flex flex-wrap items-center gap-2 max-sm:basis-full max-sm:pl-8">
          {hasDoc && claim.uhid && (
            <button type="button" className="text-[12px] text-link underline pointer-coarse:min-h-11" onClick={() => openPatientDocument(claim.uhid as string, doc.document_id as number)}>
              View
            </button>
          )}
          {missing && !waiving && (
            <>
              <DocumentUploadField
                uhid={claim.uhid || ''} category={DOC_KEY_CATEGORY[doc.doc_key] || 'Other'}
                className="border-teal/50 text-teal hover:bg-accent pointer-coarse:h-11"
                onUploaded={(documentId) => attachMutation.mutate({ docKey: doc.doc_key, documentId })}
              />
              <Button size="sm" variant="ghost" className="border-transparent pointer-coarse:h-11" onClick={() => setWaiving(true)}>Waive…</Button>
            </>
          )}
          {state === 'waived' && (
            <Button size="sm" variant="ghost" className="border-transparent pointer-coarse:h-11" onClick={() => waiveMutation.mutate({ checklistId: doc.id, waive: false })}>Un-waive</Button>
          )}
          {/* In "Needs attention" the group heading already says Missing. */}
          {!missing && <Badge variant={badge.variant}>{badge.label}</Badge>}
        </div>
      )}

      {waiving && (
        <div className="flex basis-full flex-wrap items-center gap-2 pl-8">
          <Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for waiving (required)" className="min-w-[12rem] flex-1" />
          <Button size="sm" loading={waiveMutation.isPending} onClick={doWaive}>Confirm waive</Button>
          <Button size="sm" variant="ghost" className="border-transparent" onClick={() => { setWaiving(false); setReason(''); setError(''); }}>Cancel</Button>
        </div>
      )}
      {error && <div className="basis-full pl-8 text-[11px] text-danger-ink">{error}</div>}
    </li>
  );
}

const READY_HIDDEN_KEY = 'hms_claim_docs_ready_hidden';
function readReadyHidden(): boolean {
  try { return localStorage.getItem(READY_HIDDEN_KEY) === '1'; } catch { return false; }
}

/** Rounded list with solid hairline dividers. Explicit per-row top borders,
 * not `divide-y`/`border-dashed`: this app runs Tailwind without preflight, so
 * any utility that sets border-style on all sides would light up the
 * browser-default 3px borders on the other three sides. */
function DocList({ children }: { children: ReactNode }) {
  return (
    <ul className="m-0 list-none overflow-hidden rounded-lg border border-border bg-card p-0 [&>li+li]:border-0 [&>li+li]:border-t [&>li+li]:border-solid [&>li+li]:border-border">
      {children}
    </ul>
  );
}

function GroupHeading({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-1.5 mt-1 flex items-baseline justify-between text-[10.5px] font-bold uppercase tracking-wide text-ink-muted', className)}>
      {children}
    </div>
  );
}

/** Required items ready / required items that apply — the same count as the
 * "Documents N/M" tab label. */
function docProgress(claim: ClaimWorkspaceType) {
  const required = claim.document_checklist.filter((d) => d.required && d.effective_status !== 'not_applicable');
  const ready = required.filter((d) => d.effective_status !== 'missing').length;
  return { ready, total: required.length, toGo: required.length - ready };
}

function DocProgress({ claim, short }: { claim: ClaimWorkspaceType; short?: boolean }) {
  const { ready, total, toGo } = docProgress(claim);
  const pct = total ? Math.round((ready / total) * 100) : 100;
  return (
    <div className="mb-3 flex items-center gap-3">
      <div
        role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={ready}
        aria-label="Required documents ready"
        className="h-2 flex-1 overflow-hidden rounded-full bg-muted"
      >
        <div className={cn('h-full rounded-full', toGo ? 'bg-warn' : 'bg-success', 'w-[var(--pct)]')} ref={(el) => { el?.style.setProperty('--pct', `${pct}%`); }} />
      </div>
      <span className="shrink-0 text-[12px] tabular-nums">
        <b>{ready} of {total}</b>{short ? ' required' : ' required ready'}
        {toGo > 0 && !short && <> · <b className="text-warn-ink">{toGo} to go</b></>}
      </span>
    </div>
  );
}

/** The Documents tab checklist (wireframe S3b): progress, then what still
 * needs doing ("Needs attention": required items only), optional items not
 * yet added, then what is done ("Ready": attached, generated, waived, N/A —
 * hideable, remembered per browser). `compact` (Overview right rail) lists up to three items still
 * needed and links to the full tab. */
function DocumentChecklistSection({ claim, compact, onOpen }: { claim: ClaimWorkspaceType; compact?: boolean; onOpen?: () => void }) {
  const [readyHidden, setReadyHidden] = useState(readReadyHidden);
  const toggleReady = () => {
    const next = !readyHidden;
    setReadyHidden(next);
    try { localStorage.setItem(READY_HIDDEN_KEY, next ? '1' : '0'); } catch { /* per-viewer convenience only */ }
  };

  const items = [...claim.document_checklist].sort((a, b) => a.sort_order - b.sort_order);
  const need = items.filter((d) => docState(d) === 'need');
  const optional = items.filter((d) => docState(d) === 'optional');
  const done = items.filter((d) => d.effective_status !== 'missing');
  const requiredNeed = need.length;

  if (compact) {
    const shown = need.slice(0, 3);
    const more = requiredNeed - shown.length;
    return (
      <Section title="Documents">
        <DocProgress claim={claim} short />
        {requiredNeed === 0 ? (
          <div className="text-[12.5px] text-success-ink">✓ Every required document is attached, generated or waived.</div>
        ) : (
          <DocList>
            {shown.map((d) => <DocumentChecklistRow key={d.doc_key} claim={claim} doc={d} compact />)}
            {onOpen && (
              <li className="px-3 py-2">
                <button type="button" className="text-[12px] text-link underline" onClick={onOpen}>
                  {more > 0 ? `+${more} more to do · ` : ''}Open Documents ›
                </button>
              </li>
            )}
          </DocList>
        )}
      </Section>
    );
  }

  return (
    <Section title="Document checklist">
      <DocProgress claim={claim} />
      {need.length > 0 ? (
        <>
          <GroupHeading>Needs attention · {need.length}</GroupHeading>
          <DocList>{need.map((d) => <DocumentChecklistRow key={d.doc_key} claim={claim} doc={d} />)}</DocList>
        </>
      ) : (
        <div className="mb-1 text-[12.5px] text-success-ink">✓ Every required document is attached, generated or waived.</div>
      )}
      {/* Optional items are not "attention" — kept apart so a ready claim
          does not read as having six things to fix. */}
      {optional.length > 0 && (
        <>
          <GroupHeading className="mt-4">Optional — not yet added · {optional.length}</GroupHeading>
          <DocList>{optional.map((d) => <DocumentChecklistRow key={d.doc_key} claim={claim} doc={d} />)}</DocList>
        </>
      )}
      {done.length > 0 && (
        <>
          <GroupHeading className="mt-4">
            <span>Ready · {done.length}</span>
            <button type="button" className="font-normal normal-case tracking-normal text-link underline pointer-coarse:min-h-11" aria-expanded={!readyHidden} onClick={toggleReady}>
              {readyHidden ? 'Show' : 'Hide'}
            </button>
          </GroupHeading>
          {!readyHidden && <DocList>{done.map((d) => <DocumentChecklistRow key={d.doc_key} claim={claim} doc={d} />)}</DocList>}
        </>
      )}
    </Section>
  );
}

// ── Settlement tab ──────────────────────────────────────────────────────

function SettlementTab({ claim }: { claim: ClaimWorkspaceType }) {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const canSettle = !!user?.modules?.includes('claims:settle');

  return (
    <Section title="Settlement">
      <div className="grid grid-cols-2 gap-2 text-[13px] sm:grid-cols-4">
        <div>Settled <b>{money(claim.settled_total)}</b></div>
        <div>TDS <b>{money(claim.tds_total)}</b></div>
        <div>Disallowed <b>{money(claim.disallowed_total)}</b></div>
        <div>Written off <b>{money(claim.written_off_total)}</b></div>
      </div>
      <div className="mt-3">
        {canSettle ? (
          <Button size="sm" onClick={() => setDialogOpen(true)}>Post settlement</Button>
        ) : (
          <div className="text-[12px] text-ink-muted">Posting a settlement needs the <code>claims:settle</code> grant.</div>
        )}
      </div>
      {dialogOpen && <SettlementDialog claim={claim} open onClose={() => setDialogOpen(false)} />}
    </Section>
  );
}
